import React from 'react';
import { useCNContextSelector, useCNDispatch } from './CreditNotes.context';
import { RouteComponentProps } from 'react-router';
import { IKitDetail } from 'api/swaggerTypes';

export enum CreditNotessAPIType {
    deleteGridLine = 'deleteGridLine',
    updateGridLine = 'updateGridLine',
    updateLine = 'updateLine',
}

export const useTabLocations = (url: string, location: RouteComponentProps['location']) => {
    const urlParams = new URLSearchParams(location.search);
    const contextDispatch = useCNDispatch();
    const selectedContextCreditNotes = useCNContextSelector<'CreditNote'>((state) => state.CreditNote);
    const WorkCreditNoteLineId = Number(urlParams.get('WorkCreditNoteLineId') ?? 0);
    const LineNumber = Number(urlParams.get('LineNumber') ?? 0);
    const CreditNotes = selectedContextCreditNotes;

    const tabLocations = React.useMemo(
        () => {

            return ({
                Lines: {
                    pathname: url,
                    search: `?CreditNote=${CreditNotes ?? ''}`
                },
                CreditNoteDetails: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}credit-note-details`,
                    search: `?CreditNote=${CreditNotes ?? ''}`
                },
                LineDetails: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}line-details`,
                    search: `?CreditNote=${CreditNotes ?? ''}`
                },
                Notes: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}notes`,
                    search: `?CreditNote=${CreditNotes}`
                },
                KitComponents: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}line-details/kit-components`,
                    search: `?CreditNote=${CreditNotes}&LineNumber=${LineNumber}&WorkCreditNoteLineId=${WorkCreditNoteLineId}`
                },
                ComponentDetails: {
                    pathname: `${url.endsWith('/') ? url : `${url}/`}line-details/kit-components/component-details`,
                    search: `?CreditNote=${CreditNotes}&LineNumber=${LineNumber}&WorkCreditNoteLineId=${WorkCreditNoteLineId}`
                },
            });
        },
        [url, CreditNotes, LineNumber, WorkCreditNoteLineId]
    );

    React.useEffect(
        () => {
            const params = (new URLSearchParams(location.search));
            const CreditNotesParam = params.get('CreditNotes');
            const LineNumberParam = params.get('LineNumber') || '';

            contextDispatch({
                setCreditNote: Number(CreditNotesParam),
                setLineNumber: Number(LineNumberParam),
            });
        },
        [contextDispatch]
    );

    return {
        tabLocations
    };
};

type BomApis = 'addLine' | 'calculateFastEntry' | 'updateGridLine' | 'calculateGridLine' | 'update' | 'load';

export const usePriceOverrideAndKitValidations = () => {
    const [multipleBomOpen, setMultipleBomState] = React.useState<boolean>(false);
    const [bomList, setBomList] = React.useState<IKitDetail[]>([]);
    const [kitStatus, setKitStatus] = React.useState<string>('');
    const [selectedBomCode, setSelectedBomCode] = React.useState<string>('');
    const [multipleBomDialogApi, setMultipleBomDialogApi] = React.useState<BomApis>(null);

    const resetStates = () => {
        setMultipleBomState(false);
        setBomList([]);
        setKitStatus('');
        setSelectedBomCode('');
        setMultipleBomDialogApi(null);
    };

    const validate = (response: any) => {
        if (!response.Forms) {
            return;
        }
        const form = response.Forms?.[0];

        if (form?.FormId === 'SelectBomCode') {
            setBomList((response?.KitDetail) ?? [] as any);
            setKitStatus((response?.KitStatus) ?? '');
            setSelectedBomCode((response?.BomCode) ?? '');
            setMultipleBomDialogApi(response?.multipleBomDialogApi ?? null);
            setMultipleBomState(true);
        }
    };

    return {
        bomList,
        kitStatus,
        selectedBomCode,
        multipleBomOpen,
        multipleBomDialogApi,
        resetStates,
        setMultipleBomDialogApi,
        validate,
        setBomList,
        setKitStatus,
        setSelectedBomCode,
        setMultipleBomState
    };
};
